import { useState } from 'react'
import type { FormEvent } from 'react'
import { Dialogo } from './Dialogo'
import { dinero } from '../api/formato'

interface Props {
    /** Nulo cuando el descuento va sobre el total de la venta. */
    lineaDescripcion: string | null
    base: number
    alConfirmar: (datos: { tipo: 'Monto' | 'Porcentaje'; valor: number; motivo: string }) => void
    alCerrar: () => void
}

const MOTIVOS = ['Producto dañado', 'Cliente frecuente', 'Cerca de vencer', 'Acuerdo con el dueño']

export function DialogoDescuento({ lineaDescripcion, base, alConfirmar, alCerrar }: Props) {
    const [tipo, setTipo] = useState<'Monto' | 'Porcentaje'>('Monto')
    const [texto, setTexto] = useState('')
    const [motivo, setMotivo] = useState('')

    const valor = Number.parseFloat(texto.replace(',', '.'))
    const valido =
        Number.isFinite(valor) &&
        valor > 0 &&
        (tipo === 'Monto' ? valor <= base : valor <= 100) &&
        motivo.trim().length > 0

    const importe = Number.isFinite(valor)
        ? tipo === 'Monto'
            ? Math.min(valor, base)
            : Math.round((base * Math.min(valor, 100)) / 100)
        : 0

    function enviar(evento: FormEvent) {
        evento.preventDefault()
        if (valido) alConfirmar({ tipo, valor, motivo: motivo.trim() })
    }

    return (
        <Dialogo
            titulo="Aplicar descuento"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button type="submit" form="forma-descuento" className="boton boton-primario" disabled={!valido}>
                        Aplicar
                    </button>
                </>
            }
        >
            <form id="forma-descuento" className="columna g4" onSubmit={enviar}>
                <p className="tenue chico">
                    {lineaDescripcion
                        ? `Sobre la línea: ${lineaDescripcion}`
                        : 'Sobre el total de la venta'}
                    {' · '}
                    Base {dinero(base)}
                </p>

                <div className="fila g2">
                    {(['Monto', 'Porcentaje'] as const).map((opcion) => (
                        <button
                            key={opcion}
                            type="button"
                            className={`boton crece ${tipo === opcion ? 'boton-primario' : ''}`}
                            onClick={() => setTipo(opcion)}
                        >
                            {opcion === 'Monto' ? '$ Monto' : '% Porcentaje'}
                        </button>
                    ))}
                </div>

                <div>
                    <label className="etiqueta" htmlFor="valor-descuento">
                        {tipo === 'Monto' ? 'Monto a descontar' : 'Porcentaje'}
                    </label>
                    <input
                        id="valor-descuento"
                        className="campo campo-numero"
                        value={texto}
                        onChange={(evento) => setTexto(evento.target.value)}
                        inputMode="decimal"
                        autoComplete="off"
                        required
                    />
                </div>

                <div>
                    {/* El motivo es obligatorio en el backend: se ofrecen los
                        habituales para que el cajero no escriba cualquier cosa
                        con tal de avanzar. */}
                    <label className="etiqueta" htmlFor="motivo-descuento">Motivo</label>
                    <input
                        id="motivo-descuento"
                        className="campo"
                        value={motivo}
                        onChange={(evento) => setMotivo(evento.target.value)}
                        list="motivos-frecuentes"
                        maxLength={250}
                        required
                    />
                    <datalist id="motivos-frecuentes">
                        {MOTIVOS.map((opcion) => (
                            <option key={opcion} value={opcion} />
                        ))}
                    </datalist>
                </div>

                <div className="fila entre panel" style={{ padding: 'var(--e3)' }}>
                    <span className="tenue">Se descontará</span>
                    <strong className="numero">{dinero(importe)}</strong>
                </div>
            </form>
        </Dialogo>
    )
}
